import {
  Box,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { CustomButton } from "src/design/CustomButton";
import { formatRestaurantLabel } from "src/business/restaurant/restaurantLabel";
import { TablePaginationBar } from "src/design/TablePaginationBar";
import { usePagination } from "src/helpers/usePagination";
import { colorTextMuted, colorTextPrimary } from "src/design/tokens";
import { GrantRestaurantOption } from "./GrantSubscriptionDialog";

interface UnsubscribedRestaurantsTableProps {
  restaurants: GrantRestaurantOption[];
  onGrant: (restaurant: GrantRestaurantOption) => void;
  sx?: SxProps;
}

/**
 * Restaurants with no subscription record at all. They are invisible to their
 * own staff until a subscription is attached, so ops need them listed.
 */
export const UnsubscribedRestaurantsTable = ({
  restaurants,
  onGrant,
  sx,
}: UnsubscribedRestaurantsTableProps) => {
  const { page, rowsPerPage, visibleRows, setPage, setRowsPerPage } =
    usePagination(restaurants);

  return (
    <Box sx={sx}>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ mt: -1, minWidth: 480 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: colorTextMuted, fontWeight: 600, pl: 0 }}>
                Restaurant
              </TableCell>
              <TableCell align="right" sx={{ pr: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((restaurant) => (
              <TableRow
                key={restaurant.id}
                sx={{
                  "&:last-child td": { borderBottom: 0 },
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <TableCell sx={{ pl: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: colorTextPrimary }}
                  >
                    {formatRestaurantLabel(restaurant.name, restaurant.city)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colorTextMuted, fontFamily: "monospace" }}
                  >
                    {restaurant.id}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ pr: 0 }}>
                  <CustomButton
                    color="inverted"
                    onClick={() => onGrant(restaurant)}
                  >
                    Grant
                  </CustomButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <TablePaginationBar
        count={restaurants.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    </Box>
  );
};
